import { getWorksheetsFromFile } from "@features/upload/helpers"
import logger from "@utils/logger"
import domToImage from "dom-to-image"

export const parseCsvFile = async (
  file: File,
  maxRows?: number,
): Promise<Record<string, string>[]> => {
  const fileContent = await readFile(file)

  return getTableRowsFromCsvContent(fileContent, maxRows)
}

export const getTableRowsFromCsvContent = (content: string, maxRows?: number) => {
  let rows = csvContentToRows(content)

  rows = rows.filter((row) => row)

  if (rows.length < 2) return []

  const [headerRow, ...dataRows] = rows
    .slice(0, typeof maxRows === "number" ? maxRows + 1 : undefined)
    .map((item: string) => item.split(","))

  return dataRows.map((row) => {
    const object: Record<string, string> = {}
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    headerRow.forEach((header, index) => {
      if (!header) return
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      object[header] = row[index]
    })
    return object
  })
}

export const removeEmptyRowsFromCsvFileContent = (content: string) => {
  if (content.includes("\r\n")) return content.replaceAll(/\r\n,*\r\n/gi, "\r\n\r\n")
  if (content.includes("\n")) return content.replaceAll(/\n,*\n/gi, "\n\n")
  if (content.includes("\r")) return content.replaceAll(/\r,*\r/gi, "\r\r")

  return content
}

export const readFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)

    reader.onerror = () => {
      logger.error(`Error occured while reading file "${file.name}" content`)
      reject()
    }
    reader.readAsText(file)
  })
}

export const downloadHtmlImage = async (fileName: string, htmlElement: HTMLElement, scale = 1) => {
  const style = {
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    width: `${htmlElement.offsetWidth}px`,
    height: `${htmlElement.offsetHeight}px`,
  }

  const dataURL = await domToImage.toPng(htmlElement, {
    height: htmlElement.offsetHeight * scale,
    width: htmlElement.offsetWidth * scale,
    quality: 1,
    style,
  })
  const url = dataURL.replace(/^data:image\/png/, "data:application/octet-stream")
  downloadFile(`${fileName}.png`, url)
}

export const downloadFile = (fileName: string, url: string) => {
  const anchorElement = document.createElement("a")
  anchorElement.setAttribute("download", fileName)
  anchorElement.setAttribute("href", url)
  anchorElement.click()
}

export const stringToFile = (
  fileName: string,
  fileContent: string,
  type: string = "text/plain",
) => {
  const blob = new Blob([fileContent], { type })

  return new File([blob], fileName, { type })
}

export const csvContentToRows = (csvContent: string) => {
  // on different systems, end of line character may differ
  let rows = csvContent.split("\r\n") // windows - CRLF
  if (rows.length < 2) rows = csvContent.split("\n") // linux - LF
  if (rows.length < 2) rows = csvContent.split("\r") // macOS - CR

  return rows
}

export const getFileFormat = (file: File) => {
  const fileNameParts = file.name.split(".")
  if (fileNameParts.length < 2) return ""

  return `.${fileNameParts[fileNameParts.length - 1]}`
}

export const isExcelFileEmpty = async (file: File) => {
  // Limit number of rows to 1 as we only need to check if there's any data
  const worksheets = await getWorksheetsFromFile(file, 1)

  return worksheets.every((ws) => ws.tables.every((t) => t.rows.length === 0))
}

export const rowsToCsvContent = (rows: Record<string, string>[]): string => {
  const headerRow = rows[0]
  if (!headerRow) return ""

  const headers = Object.keys(headerRow)
  const csvContent = [headers.join(",")]

  rows.forEach((row) => {
    const dataRow = headers.map((header) => row[header] ?? "")
    csvContent.push(dataRow.join(","))
  })

  return csvContent.join("\n")
}
